import posthog from "posthog-js"
import type { BeforeSendFn, CaptureResult } from "posthog-js"
import { matchRoutes } from "react-router"
import type { RouteObject } from "react-router"

import { IS_PROD } from "@/lib/constants"

type IdentifiedUser = {
  pid: string
  email: string | null | undefined
  name: string | null | undefined
  isAdmin: boolean | null | undefined
  isImpersonated: boolean | null | undefined
  org: { pid: string; slug: string; name: string } | null | undefined
}

// Clicking into a text field is a "dead click" by PostHog's definition — the
// caret moves and nothing else on the page changes — so these fire constantly
// and drown out the real signal. A click on a *disabled* field is genuinely
// dead in the way we care about, so those are kept.
const FORM_FIELD_TAGS = new Set(["textarea", "input", "select"])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const isDisabled = (element: Record<string, unknown>) => {
  // Autocapture serializes DOM attributes as `attr__<name>`. A bare `disabled`
  // attribute comes through as the empty string, so presence is what counts.
  if (!("attr__disabled" in element)) {
    return false
  }
  const disabled = element["attr__disabled"]
  return disabled != null && disabled !== "false"
}

// The element chain runs from the clicked element outward to its ancestors.
const clickedElement = (event: CaptureResult): Record<string, unknown> | null => {
  const properties: unknown = event.properties
  if (!isRecord(properties) || !("$elements" in properties)) {
    return null
  }
  const elements = properties["$elements"]
  if (!Array.isArray(elements)) {
    return null
  }
  const clicked: unknown = elements[0]
  return isRecord(clicked) ? clicked : null
}

const dropDeadClicksOnEnabledFormFields: BeforeSendFn = (event) => {
  if (event?.event !== "$dead_click") {
    return event
  }
  const clicked = clickedElement(event)
  if (clicked === null || !("tag_name" in clicked)) {
    return event
  }
  const tagName = clicked["tag_name"]
  if (typeof tagName !== "string") {
    return event
  }
  if (FORM_FIELD_TAGS.has(tagName.toLowerCase()) && !isDisabled(clicked)) {
    return null
  }
  return event
}

function enrichPageview(
  event: CaptureResult | null,
  routes: RouteObject[],
): CaptureResult | null {
  if (event?.event !== "$pageview") return event

  const currentUrl: unknown = event.properties["$current_url"]
  if (typeof currentUrl !== "string") return event

  let pathname: string
  try {
    // Use the event's URL rather than window.location so navigation timing
    // cannot attach a different page's route to a captured pageview.
    pathname = new URL(currentUrl).pathname
  } catch {
    return event
  }

  const route = matchRoutes(routes, pathname)?.findLast(({ route }) => route.path)
    ?.route.path
  if (route) {
    event.properties["normalized_path"] = route
  }
  return event
}

export function identifyUser({
  pid,
  email,
  name,
  isAdmin,
  isImpersonated,
  org,
}: IdentifiedUser) {
  // Record request state on events, not just the mutable person profile.
  posthog.register({ is_impersonated: isImpersonated ?? false })

  if (isAdmin) {
    posthog.resetGroups()
  }

  posthog.identify(pid, {
    email: email ?? undefined,
    name: name ?? undefined,
    is_admin: isAdmin ?? undefined,
    is_impersonated: isImpersonated ?? undefined,
  })

  if (org && !isAdmin) {
    // Grouping automatic and product events lets PostHog insights compare how
    // different organizations use the app without putting organization names
    // into event payloads.
    posthog.group("organization", org.pid, { slug: org.slug, name: org.name })
  }
}

// Production sends to the prod project; staging and local dev send to the
// staging/dev project. Staging and prod share this bundle, so the choice is
// made on the serving origin rather than at build time.
export function initPosthog(routes: RouteObject[]) {
  const projectToken = IS_PROD
    ? import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN
    : import.meta.env.VITE_PUBLIC_POSTHOG_DEV_PROJECT_TOKEN
  if (!projectToken) return

  posthog.init(projectToken, {
    // Managed reverse proxy on our own domain, so ingestion isn't blocked by
    // ad/tracker blockers that match posthog.com.
    api_host: "https://t.lumen.energy",
    // The proxy only fronts ingestion. Links out to the PostHog app (toolbar,
    // session replay) have to target the real UI host or they 404.
    ui_host: "https://us.posthog.com",
    defaults: "2026-05-30",
    // Already the SDK default; stated explicitly because it decides whether
    // anonymous visitors get person profiles, which is what we're billed on.
    person_profiles: "identified_only",
    // Authentication must confirm impersonation on each page load.
    loaded: (client) => client.unregister("is_impersonated"),
    // Capture navigation and UI interactions automatically, including SPA
    // history changes. Domain events in analytics.ts supplement this baseline.
    autocapture: true,
    capture_pageview: "history_change",
    capture_exceptions: true,
    before_send: [
      dropDeadClicksOnEnabledFormFields,
      (event) => enrichPageview(event, routes),
    ],
  })
}

export { posthog, dropDeadClicksOnEnabledFormFields }
